import { client, authHeaders } from '../../_shared/services/openapiClient';
import type { paths } from '../../../types/schema';

export type UpdateBettieProfileBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}']['put']['requestBody']
>['content']['application/json'];
export type UpdateBettieProfileResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}']['put']['responses'][200]['content']['application/json'];

export type GetBettieChatRoomPicturesResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/pictures']['get']['responses'][200]['content']['application/json']['bettiePictures'];

export type SwitchBettieProfilePictureBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/pictures']['put']['requestBody']
>['content']['application/json'];
export type SwitchBettieProfilePictureResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/pictures']['put']['responses'][200]['content']['application/json'];

export type GetBettieRecommendPicturesResponse =
  paths['/api/v1/bettie/recommend_pictures']['get']['responses'][200]['content']['application/json'];

export type DeleteBettieProfilePictureResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/pictures']['delete']['responses'][200]['content']['application/json'];

export type ToggleBettieProfilePictureVisibilityBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/pictures/private']['post']['requestBody']
>['content']['application/json'];
export type ToggleBettieProfilePictureVisibilityResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/pictures/private']['post']['responses'][200]['content']['application/json'];

export async function updateBettieProfile(
  body: UpdateBettieProfileBody,
  chatRoomId: string
): Promise<UpdateBettieProfileResponse> {
  const { data, error } = await client.PUT('/api/v1/bettie/chat_rooms/{chat_room_id}', {
    params: { path: { chat_room_id: chatRoomId } },
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie profile');
  return data;
}

export async function getBettieChatRoomPictures(
  chatRoomId: string
): Promise<GetBettieChatRoomPicturesResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/chat_rooms/{chat_room_id}/pictures', {
    params: { path: { chat_room_id: chatRoomId } },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie chat room pictures');
  return data.bettiePictures;
}

export async function switchBettieProfilePicture(
  chatRoomId: string,
  body: SwitchBettieProfilePictureBody
): Promise<SwitchBettieProfilePictureResponse> {
  const { data, error } = await client.PUT('/api/v1/bettie/chat_rooms/{chat_room_id}/pictures', {
    params: { path: { chat_room_id: chatRoomId } },
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when switching bettie profile picture');
  return data;
}

export async function getBettieRecommendPictures(
  chatRoomId: string,
  memberId?: string,
  pageParam = 1
): Promise<GetBettieRecommendPicturesResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/recommend_pictures', {
    params: {
      // NOTE: 현재 schema.d.ts에는 page/per_page가 없지만 응답은 meta.pagination을 포함합니다.
      query: {
        chat_room_id: chatRoomId,
        member_id: memberId,
        page: pageParam,
        per_page: 5
      } as any
    },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie recommend pictures');
  return data;
}

export async function deleteBettieProfilePicture(
  chatRoomId: string
): Promise<DeleteBettieProfilePictureResponse> {
  const { data, error } = await client.DELETE('/api/v1/bettie/chat_rooms/{chat_room_id}/pictures', {
    params: { path: { chat_room_id: chatRoomId } },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when deleting bettie profile picture');
  return data;
}

export async function toggleBettieProfilePictureVisibility(
  chatRoomId: string,
  body: ToggleBettieProfilePictureVisibilityBody
): Promise<ToggleBettieProfilePictureVisibilityResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/pictures/private',
    {
      params: { path: { chat_room_id: chatRoomId } },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when toggling bettie profile picture visibility');
  return data;
}
